<script setup>
import { ref, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
    profileUrl: { type: String, required: true },
    emailUrl: { type: String, required: true },
    passwordUrl: { type: String, required: true },
    logoutUrl: { type: String, required: true },
})

const items = [
    { label: 'My Presses', href: props.profileUrl },
    { label: 'Manage Email', href: props.emailUrl },
    { label: 'Change Password', href: props.passwordUrl },
    { label: 'Logout', href: props.logoutUrl },
]

const open = ref(false)
const rootEl = ref(null)
const triggerEl = ref(null)
const itemRefs = ref([])

const focusItem = (index) => {
    const count = itemRefs.value.length
    if (count === 0) return
    const next = ((index % count) + count) % count
    itemRefs.value[next]?.focus()
}

const openMenu = (focusIndex = 0) => {
    open.value = true
    document.addEventListener('click', onClickOutside)
    nextTick(() => focusItem(focusIndex))
}

const closeMenu = ({ refocusTrigger = true } = {}) => {
    open.value = false
    document.removeEventListener('click', onClickOutside)
    if (refocusTrigger) {
        triggerEl.value?.focus()
    }
}

const toggleMenu = () => {
    if (open.value) {
        closeMenu()
    } else {
        openMenu(0)
    }
}

const onTriggerKeydown = (e) => {
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        openMenu(0)
    } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        openMenu(items.length - 1)
    }
}

const currentIndex = () => itemRefs.value.indexOf(document.activeElement)

const onMenuKeydown = (e) => {
    const idx = currentIndex()

    switch (e.key) {
        case 'Escape':
            e.preventDefault()
            closeMenu()
            break
        case ' ':
            // Prevent the page from scrolling; activate the focused item
            // the same way Enter would.
            e.preventDefault()
            document.activeElement?.click()
            break
        case 'ArrowDown':
            e.preventDefault()
            focusItem(idx + 1)
            break
        case 'ArrowUp':
            e.preventDefault()
            focusItem(idx - 1)
            break
        case 'Home':
            e.preventDefault()
            focusItem(0)
            break
        case 'End':
            e.preventDefault()
            focusItem(itemRefs.value.length - 1)
            break
        case 'Tab':
            // Menu items use tabindex="-1" so they are never part of the
            // natural tab order themselves. Close the menu and let the
            // browser's default Tab handling move focus to whatever comes
            // next in the page, instead of trapping focus inside.
            closeMenu({ refocusTrigger: false })
            break
        default:
            break
    }
}

const onClickOutside = (e) => {
    if (open.value && rootEl.value && !rootEl.value.contains(e.target)) {
        closeMenu({ refocusTrigger: false })
    }
}

// The click-outside listener is only attached while the menu is open
// (see openMenu/closeMenu) so it can't accumulate or outlive the menu's
// lifetime; this is a safety net in case the component is torn down
// while the menu is still open.
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
    <div class="c-header__dropdown" ref="rootEl">
        <button
            id="account-menu-trigger"
            ref="triggerEl"
            type="button"
            class="c-header__dropdown-trigger"
            aria-haspopup="menu"
            aria-controls="account-menu-list"
            :aria-expanded="open ? 'true' : 'false'"
            @click="toggleMenu"
            @keydown="onTriggerKeydown"
        >
            My Account
        </button>

        <ul
            v-show="open"
            id="account-menu-list"
            role="menu"
            aria-labelledby="account-menu-trigger"
            class="c-header__dropdown-menu"
            @keydown="onMenuKeydown"
        >
            <li v-for="item in items" :key="item.href" role="none">
                <a
                    role="menuitem"
                    tabindex="-1"
                    ref="itemRefs"
                    :href="item.href"
                    class="c-header__dropdown-link"
                    @click="() => closeMenu()"
                >
                    {{ item.label }}
                </a>
            </li>
        </ul>
    </div>
</template>
